<template>
  <div v-loading="loading" class="warehouse-opt-page">
    <div class="filter-panel">
      <div class="filter-grid">
        <div class="field">
          <label>统计年度</label>
          <el-date-picker
            v-model="yearModel"
            type="year"
            value-format="yyyy"
            placeholder="选择年度"
            size="small"
            :clearable="false"
          />
        </div>
        <div class="field">
          <label>煤棚</label>
          <el-select v-model="filters.shedName" size="small" clearable placeholder="全部煤棚">
            <el-option v-for="item in shedOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </div>
        <div class="field">
          <label>客户</label>
          <el-select
            v-model="filters.customerId"
            size="small"
            clearable
            filterable
            remote
            reserve-keyword
            placeholder="请选择客户"
            :remote-method="searchCustomerOptions"
            :loading="customerLoading"
            @change="onCustomerChange"
            @clear="onCustomerClear"
          >
            <el-option
              v-for="item in customerOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="field">
          <label>物资</label>
          <el-select
            v-model="filters.cargoId"
            size="small"
            clearable
            filterable
            remote
            reserve-keyword
            placeholder="请选择物资"
            :remote-method="searchCargoOptions"
            :loading="cargoLoading"
            @change="onCargoChange"
            @clear="onCargoClear"
          >
            <el-option
              v-for="item in cargoOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="field">
          <label>高频阈值</label>
          <el-input :value="'≥ ' + highFreqThreshold + ' 次/年'" size="small" disabled />
        </div>
        <div class="filter-actions">
          <el-button size="small" type="primary" @click="fetchData">查询</el-button>
          <el-button size="small" @click="resetFilters">重置</el-button>
        </div>
      </div>
    </div>

    <div class="metric-grid">
      <div class="metric-card score-card">
        <div class="score-ring" :style="scoreRingStyle">
          <span>{{ layoutScoreText }}</span>
        </div>
        <div class="score-copy">
          <div class="metric-name">布局优化评分</div>
          <div class="metric-note">依据高频库位、周转率、容量利用率和客户物资分散情况综合展示</div>
          <div class="metric-trend" :class="scoreClass">整体可优化空间：{{ optimizeLevelText }}</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-name">高频库位</div>
        <div class="metric-value danger">{{ highFrequencyCount }} 个</div>
        <div class="metric-note">建议靠近主通道或装卸口</div>
      </div>
      <div class="metric-card">
        <div class="metric-name">可合并建议</div>
        <div class="metric-value primary">{{ mergeCount }} 项</div>
        <div class="metric-note">同客户、同物资分散存储</div>
      </div>
      <div class="metric-card">
        <div class="metric-name">容量风险</div>
        <div class="metric-value warning">{{ capacityRiskCount }} 项</div>
        <div class="metric-note">容量不足或利用率偏高</div>
      </div>
      <div class="metric-card">
        <div class="metric-name">当前库存/容量</div>
        <div class="metric-value">{{ fmtTenThousand(totalStockTons) }}</div>
        <div class="metric-note">平均容量利用率 {{ fmtPercent(avgCapacityRate) }}</div>
      </div>
    </div>

    <div class="workbench-grid">
      <div class="panel">
        <div class="section-head">
          <div>
            <div class="section-title">库位热度与容量利用矩阵</div>
            <div class="section-tip">颜色代表年度作业频率，右上角展示容量利用率；橙色描边表示需人工复核。</div>
          </div>
          <div class="legend">
            <span><i class="legend-dot hot" />高频</span>
            <span><i class="legend-dot busy" />繁忙</span>
            <span><i class="legend-dot steady" />稳定</span>
            <span><i class="legend-dot low" />低频</span>
          </div>
        </div>
        <div v-if="groupedBinRows.length === 0" class="empty-box">暂无库位数据</div>
        <div v-else class="heat-wrap">
          <div v-for="group in groupedBinRows" :key="group.shedName" class="shed-row">
            <div class="shed-label">{{ group.shedName }}</div>
            <div class="heat-grid">
              <div
                v-for="row in group.rows"
                :key="rowKey(row)"
                class="heat-cell"
                :class="[heatClass(row), { review: needsReview(row) }]"
                @click="openVariation(row)"
              >
                <div class="cell-rate">{{ capacityRateText(row) }}</div>
                <div class="cell-code">{{ binShortLabel(row) }}</div>
                <div class="cell-meta">
                  {{ row.custName || '-' }} / {{ row.cargoName || '-' }}<br />
                  频率 {{ row.annualFrequency || 0 }} 次
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="panel priority-panel">
        <div class="section-head">
          <div class="section-title">建议优先级队列</div>
          <el-tag size="mini" type="info">需人工复核后执行</el-tag>
        </div>
        <div class="priority-list">
          <div v-for="item in priorityItems" :key="item.key" class="priority-item">
            <div class="priority-top">
              <div class="priority-title">{{ item.title }}</div>
              <el-tag size="mini" :type="item.tagType">{{ item.tag }}</el-tag>
            </div>
            <div class="priority-desc">{{ item.desc }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="chart-grid">
      <div class="panel">
        <div class="section-head">
          <div class="section-title">年度出入库频率排行</div>
          <span class="section-tip">单位：次/年</span>
        </div>
        <div ref="freqChartRef" class="chart-container" />
      </div>
      <div class="panel">
        <div class="section-head">
          <div class="section-title">年度周转率排行</div>
          <span class="section-tip">单位：次/年</span>
        </div>
        <div ref="turnoverChartRef" class="chart-container" />
      </div>
    </div>

    <div class="suggest-grid">
      <div class="panel">
        <div class="section-head">
          <div class="section-title">合并/分流建议</div>
          <span class="section-tip">同客户、同物资、分散库位优先评估</span>
        </div>
        <el-table :data="mergeList" border stripe size="small" max-height="280" empty-text="暂无合并建议">
          <el-table-column prop="owner" label="客户" min-width="140" show-overflow-tooltip />
          <el-table-column prop="coalType" label="物资" min-width="100" show-overflow-tooltip />
          <el-table-column prop="targetBinLabel" label="目标库位" min-width="130" show-overflow-tooltip />
          <el-table-column label="源库位" min-width="180" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ (row.sourceBinLabels || []).join('，') || '-' }}</template>
          </el-table-column>
          <el-table-column label="合并后库存" min-width="120" align="right">
            <template slot-scope="{ row }">{{ fmtNum(row.totalStock) }} t</template>
          </el-table-column>
          <el-table-column label="可行性" min-width="110">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.canMerge ? 'primary' : 'warning'">
                {{ row.canMerge ? '建议合并' : '需分流' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="panel">
        <div class="section-head">
          <div class="section-title">执行影响摘要</div>
          <span class="section-tip">执行前需现场复核</span>
        </div>
        <div class="impact-grid">
          <div v-for="item in impactItems" :key="item.key" class="impact-item">
            <div class="impact-name">{{ item.name }}</div>
            <div class="impact-value" :class="item.color">{{ item.value }}</div>
            <div class="impact-note">{{ item.note }}</div>
          </div>
        </div>
        <div class="review-grid">
          <div class="review-card">
            <strong>容量不足</strong>
            <p>合并后库存超过目标库位容量时，只生成分流建议，不给出自动合并结论。</p>
          </div>
          <div class="review-card">
            <strong>数据缺失</strong>
            <p>客户、物资、容量为空的库位进入复核清单，避免影响评分准确性。</p>
          </div>
          <div class="review-card">
            <strong>追溯明细</strong>
            <p>库位可打开变动明细弹窗，核对入库单、库存流水和计划来源。</p>
          </div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="section-head table-head">
        <div>
          <div class="section-title">库位基础数据</div>
          <div class="section-tip">保留现有接口字段，作为所有优化建议的数据依据。</div>
        </div>
        <el-tabs v-model="activeTableTab" class="table-tabs" @tab-click="onResize">
          <el-tab-pane label="全部库位" name="all" />
          <el-tab-pane label="高频库位" name="high" />
          <el-tab-pane label="可合并" name="merge" />
          <el-tab-pane label="需复核" name="review" />
        </el-tabs>
      </div>
      <el-table :data="tableRows" border stripe size="small" max-height="420" empty-text="暂无堆位数据">
        <el-table-column prop="shedName" label="煤棚" min-width="90" show-overflow-tooltip />
        <el-table-column label="堆位" min-width="110" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.locationName || row.binLabel || '-' }}</template>
        </el-table-column>
        <el-table-column prop="custName" label="客户" min-width="130" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="物资" min-width="100" show-overflow-tooltip />
        <el-table-column label="库存(吨)" min-width="110" align="right">
          <template slot-scope="{ row }">{{ fmtNum(row.stockTons) }}</template>
        </el-table-column>
        <el-table-column label="容量(吨)" min-width="110" align="right">
          <template slot-scope="{ row }">{{ fmtNum(row.capacityTons) }}</template>
        </el-table-column>
        <el-table-column label="容量利用率" min-width="110" align="right">
          <template slot-scope="{ row }">{{ capacityRateText(row) }}</template>
        </el-table-column>
        <el-table-column prop="annualFrequency" label="出入库频率(次/年)" min-width="140" align="right" />
        <el-table-column label="周转率(次/年)" min-width="120" align="right">
          <template slot-scope="{ row }">{{ fmtNum(row.annualTurnover) }}</template>
        </el-table-column>
        <el-table-column label="建议类型" min-width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="recommendTagType(row)">{{ recommendType(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="110" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openVariation(row)">查看明细</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <variation-info ref="variationInfo" />
  </div>
</template>

<script>
import * as echarts from "echarts";
import { getCargoOptions, getCustomerOptions, getWarehouseLayoutOptimization } from "./api";
import VariationInfo from "./variation-info/index";

export default {
  name: "WarehouseLayoutOptimization",
  components: {
    VariationInfo,
  },
  data() {
    const y = String(new Date().getFullYear());
    return {
      loading: false,
      yearModel: y,
      highFreqThreshold: 100,
      binRows: [],
      highFreqBins: [],
      mergeList: [],
      freqChart: null,
      turnoverChart: null,
      activeTableTab: "all",
      customerLoading: false,
      cargoLoading: false,
      customerOptions: [],
      cargoOptions: [],
      filters: {
        shedName: "",
        customerId: "",
        customerName: "",
        cargoId: "",
        cargoName: "",
      },
    };
  },
  computed: {
    filteredRows() {
      const customerName = (this.filters.customerName || "").trim();
      const cargoName = (this.filters.cargoName || "").trim();
      return this.binRows.filter((row) => {
        const matchShed = !this.filters.shedName || row.shedName === this.filters.shedName;
        const matchCustomer = !customerName || String(row.custName || "").indexOf(customerName) > -1;
        const matchCargo = !cargoName || String(row.cargoName || "").indexOf(cargoName) > -1;
        return matchShed && matchCustomer && matchCargo;
      });
    },
    shedOptions() {
      const map = {};
      this.binRows.forEach((row) => {
        if (row.shedName) {
          map[row.shedName] = true;
        }
      });
      return Object.keys(map).sort();
    },
    groupedBinRows() {
      const groups = {};
      this.filteredRows.forEach((row) => {
        const shedName = row.shedName || "未分煤棚";
        if (!groups[shedName]) {
          groups[shedName] = [];
        }
        groups[shedName].push(row);
      });
      return Object.keys(groups)
        .sort()
        .map((shedName) => ({
          shedName,
          rows: groups[shedName].sort((a, b) => this.binSortName(a).localeCompare(this.binSortName(b))),
        }));
    },
    highFrequencyCount() {
      return this.filteredRows.filter((row) => this.isHighFrequency(row)).length;
    },
    mergeCount() {
      return this.mergeList.length;
    },
    nonMergeSuggestions() {
      return this.mergeList.filter((item) => item && item.canMerge === false);
    },
    capacityRiskRows() {
      return this.filteredRows.filter((row) => this.hasCapacityRisk(row));
    },
    capacityRiskCount() {
      return this.capacityRiskRows.length + this.nonMergeSuggestions.length;
    },
    totalStockTons() {
      return this.filteredRows.reduce((sum, row) => sum + this.toNumber(row.stockTons), 0);
    },
    totalCapacityTons() {
      return this.filteredRows.reduce((sum, row) => sum + this.toNumber(row.capacityTons), 0);
    },
    avgCapacityRate() {
      if (this.totalCapacityTons <= 0) {
        return 0;
      }
      return (this.totalStockTons / this.totalCapacityTons) * 100;
    },
    layoutScore() {
      if (this.filteredRows.length === 0) {
        return 0;
      }
      const score = 96 - this.highFrequencyCount * 2 - this.mergeCount - this.capacityRiskCount * 3;
      return Math.max(60, Math.min(96, Math.round(score)));
    },
    layoutScoreText() {
      return this.filteredRows.length === 0 ? "-" : String(this.layoutScore);
    },
    scoreRingStyle() {
      const deg = this.filteredRows.length === 0 ? 0 : Math.round((this.layoutScore / 100) * 360);
      return {
        background: "conic-gradient(#13a86b 0 " + deg + "deg, #edf1f6 " + deg + "deg 360deg)",
      };
    },
    optimizeLevelText() {
      if (this.filteredRows.length === 0) {
        return "暂无数据";
      }
      if (this.layoutScore >= 86) {
        return "较低";
      }
      if (this.layoutScore >= 76) {
        return "中高";
      }
      return "高";
    },
    scoreClass() {
      if (this.layoutScore >= 86) {
        return "green";
      }
      if (this.layoutScore >= 76) {
        return "orange";
      }
      return "danger";
    },
    mergeBinLabelMap() {
      const map = {};
      this.mergeList.forEach((item) => {
        if (item.targetBinLabel) {
          map[item.targetBinLabel] = true;
        }
        (item.sourceBinLabels || []).forEach((label) => {
          map[label] = true;
        });
      });
      return map;
    },
    tableRows() {
      if (this.activeTableTab === "high") {
        return this.filteredRows.filter((row) => this.isHighFrequency(row));
      }
      if (this.activeTableTab === "merge") {
        return this.filteredRows.filter((row) => this.isMergeCandidate(row));
      }
      if (this.activeTableTab === "review") {
        return this.filteredRows.filter((row) => this.needsReview(row));
      }
      return this.filteredRows;
    },
    priorityItems() {
      const highNames = this.filteredRows
        .filter((row) => this.isHighFrequency(row))
        .slice(0, 4)
        .map((row) => row.binLabel || this.binShortLabel(row))
        .join("、");
      return [
        {
          key: "high",
          title: "P1 高频库位前置",
          tag: this.highFrequencyCount + " 个库位",
          tagType: "danger",
          desc: (highNames || "暂无高频库位") + "。高频库位建议集中布置在靠近主通道、翻卸衔接和装车出口区域，减少搬运距离。",
        },
        {
          key: "merge",
          title: "P2 同客户同物资合并",
          tag: this.mergeCount + " 项建议",
          tagType: "primary",
          desc: "存在同客户、同物资分散存储时，优先选择容量最大的目标库位进行集中，容量不足时转为分流建议。",
        },
        {
          key: "capacity",
          title: "P3 容量不足分流",
          tag: this.capacityRiskCount + " 项风险",
          tagType: "warning",
          desc: "目标库位合并后库存超过容量上限，或单库位容量利用率超过 90% 时，不建议直接合并。",
        },
        {
          key: "review",
          title: "P4 数据异常复核",
          tag: this.tableRows.filter((row) => this.needsReview(row)).length + " 条",
          tagType: "info",
          desc: "库存为 0 但存在出入库频率、客户/物资为空、容量为空等数据只做提示，不纳入自动执行结论。",
        },
      ];
    },
    impactItems() {
      const sourceCount = this.mergeList.reduce((sum, item) => sum + ((item.sourceBinLabels || []).length), 0);
      const canMergeCount = this.mergeList.filter((item) => item && item.canMerge !== false).length;
      return [
        {
          key: "reduce",
          name: "预计减少分散库位",
          value: sourceCount + " 个",
          color: "primary",
          note: "降低同客户同物资碎片化",
        },
        {
          key: "merge",
          name: "可直接合并建议",
          value: canMergeCount + " 项",
          color: "green",
          note: "容量满足时可进入人工执行评估",
        },
        {
          key: "review",
          name: "需人工复核",
          value: this.capacityRiskCount + " 条",
          color: "orange",
          note: "容量不足、数据缺失、库存异常",
        },
        {
          key: "rate",
          name: "平均容量利用率",
          value: this.fmtPercent(this.avgCapacityRate),
          color: "danger",
          note: "按当前筛选范围汇总展示",
        },
      ];
    },
  },
  mounted() {
    this.fetchData();
    this.searchCustomerOptions("");
    this.searchCargoOptions("");
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.disposeCharts();
  },
  methods: {
    toNumber(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    fmtNum(v, digits) {
      if (v === null || v === undefined || v === "") {
        return "-";
      }
      const fixedDigits = digits === undefined ? 2 : digits;
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(fixedDigits) : String(v);
    },
    fmtPercent(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2) + "%" : "-";
    },
    fmtTenThousand(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) {
        return "-";
      }
      if (Math.abs(n) >= 10000) {
        return (n / 10000).toFixed(2) + " 万吨";
      }
      return n.toFixed(2) + " 吨";
    },
    rowKey(row) {
      return row.storeSubId || row.locationCode || row.binLabel || this.binShortLabel(row);
    },
    binSortName(row) {
      return row.locationName || row.locationCode || row.binLabel || "";
    },
    binShortLabel(row) {
      return row.locationName || row.locationCode || row.binLabel || "-";
    },
    capacityRate(row) {
      const capacity = this.toNumber(row.capacityTons);
      if (capacity <= 0) {
        return 0;
      }
      return (this.toNumber(row.stockTons) / capacity) * 100;
    },
    capacityRateText(row) {
      const capacity = this.toNumber(row.capacityTons);
      if (capacity <= 0) {
        return "-";
      }
      return this.capacityRate(row).toFixed(0) + "%";
    },
    isHighFrequency(row) {
      return this.toNumber(row.annualFrequency) >= this.highFreqThreshold;
    },
    hasCapacityRisk(row) {
      const capacity = this.toNumber(row.capacityTons);
      return capacity <= 0 || this.capacityRate(row) >= 90;
    },
    isMergeCandidate(row) {
      return !!this.mergeBinLabelMap[row.binLabel];
    },
    needsReview(row) {
      const missingBase = !row.custName || !row.cargoName || this.toNumber(row.capacityTons) <= 0;
      const emptyButActive = this.toNumber(row.stockTons) <= 0 && this.toNumber(row.annualFrequency) > 0;
      return missingBase || emptyButActive || this.hasCapacityRisk(row);
    },
    heatClass(row) {
      const freq = this.toNumber(row.annualFrequency);
      if (freq >= this.highFreqThreshold) {
        return "hot";
      }
      if (freq >= this.highFreqThreshold * 0.8) {
        return "busy";
      }
      if (freq >= this.highFreqThreshold * 0.5) {
        return "steady";
      }
      return "low";
    },
    recommendType(row) {
      if (this.needsReview(row)) {
        return "需复核";
      }
      if (this.isHighFrequency(row)) {
        return "高频前置";
      }
      if (this.isMergeCandidate(row)) {
        return "合并候选";
      }
      return "常规库位";
    },
    recommendTagType(row) {
      if (this.needsReview(row)) {
        return "warning";
      }
      if (this.isHighFrequency(row)) {
        return "danger";
      }
      if (this.isMergeCandidate(row)) {
        return "primary";
      }
      return "info";
    },
    resetFilters() {
      this.filters = {
        shedName: "",
        customerId: "",
        customerName: "",
        cargoId: "",
        cargoName: "",
      };
      this.yearModel = String(new Date().getFullYear());
      this.activeTableTab = "all";
      this.fetchData();
    },
    normalizeList(res) {
      const data = res && res.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      if (data && data.data && Array.isArray(data.data.records)) {
        return data.data.records;
      }
      if (data && Array.isArray(data.records)) {
        return data.records;
      }
      return [];
    },
    normalizeCustomerOption(item) {
      const label = item.deptName || item.custName || item.name || item.label || "";
      const value = item.id || item.deptId || item.custNo || label;
      return {
        label,
        value,
        raw: item,
      };
    },
    normalizeCargoOption(item) {
      const label = item.cargoName || item.varName || item.name || item.label || "";
      const value = item.id || item.varNo || item.cargoNo || label;
      return {
        label,
        value,
        raw: item,
      };
    },
    uniqueOptions(options) {
      const map = {};
      return options.filter((item) => {
        if (!item.label || map[item.value]) {
          return false;
        }
        map[item.value] = true;
        return true;
      });
    },
    searchCustomerOptions(keyword) {
      this.customerLoading = true;
      getCustomerOptions(keyword ? { deptName: keyword } : {})
        .then((res) => {
          this.customerOptions = this.uniqueOptions(this.normalizeList(res).map(this.normalizeCustomerOption));
        })
        .catch(() => {
          this.customerOptions = [];
        })
        .finally(() => {
          this.customerLoading = false;
        });
    },
    searchCargoOptions(keyword) {
      this.cargoLoading = true;
      getCargoOptions(keyword ? { cargoName: keyword } : {})
        .then((res) => {
          this.cargoOptions = this.uniqueOptions(this.normalizeList(res).map(this.normalizeCargoOption));
        })
        .catch(() => {
          this.cargoOptions = [];
        })
        .finally(() => {
          this.cargoLoading = false;
        });
    },
    onCustomerChange(value) {
      const option = this.customerOptions.find((item) => item.value === value);
      this.filters.customerName = option ? option.label : "";
    },
    onCustomerClear() {
      this.filters.customerName = "";
    },
    onCargoChange(value) {
      const option = this.cargoOptions.find((item) => item.value === value);
      this.filters.cargoName = option ? option.label : "";
    },
    onCargoClear() {
      this.filters.cargoName = "";
    },
    fetchData() {
      let year = this.yearModel ? parseInt(this.yearModel, 10) : new Date().getFullYear();
      if (!Number.isFinite(year)) {
        year = new Date().getFullYear();
      }
      this.loading = true;
      getWarehouseLayoutOptimization({ year })
        .then((res) => {
          const data = (res && res.data && res.data.data) || {};
          this.highFreqThreshold = data.highFrequencyThreshold != null ? data.highFrequencyThreshold : 100;
          this.binRows = Array.isArray(data.bins) ? data.bins : [];
          this.highFreqBins = Array.isArray(data.highFrequencyBins) ? data.highFrequencyBins : [];
          this.mergeList = Array.isArray(data.mergeSuggestions) ? data.mergeSuggestions : [];
          this.$nextTick(() => this.renderCharts());
        })
        .catch((e) => {
          const msg =
            (e && e.response && e.response.data && e.response.data.msg) ||
            (e && e.message) ||
            "加载失败";
          this.$message.error(msg);
          this.binRows = [];
          this.highFreqBins = [];
          this.mergeList = [];
          this.$nextTick(() => this.renderCharts());
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openVariation(row) {
      if (!row || !row.storeSubId) {
        this.$message.warning("当前库位缺少明细标识，无法查看变动明细");
        return;
      }
      if (this.$refs.variationInfo && this.$refs.variationInfo.open) {
        this.$refs.variationInfo.open(row.storeSubId);
      }
    },
    onResize() {
      if (this.freqChart) {
        this.freqChart.resize();
      }
      if (this.turnoverChart) {
        this.turnoverChart.resize();
      }
    },
    disposeCharts() {
      if (this.freqChart) {
        this.freqChart.dispose();
        this.freqChart = null;
      }
      if (this.turnoverChart) {
        this.turnoverChart.dispose();
        this.turnoverChart = null;
      }
    },
    chartAxisLabels(rows) {
      if (!rows || !rows.length) {
        return [];
      }
      return rows.map((row) => row.binLabel || this.binShortLabel(row));
    },
    topChartRows(prop) {
      return this.filteredRows
        .slice()
        .sort((a, b) => this.toNumber(b[prop]) - this.toNumber(a[prop]))
        .slice(0, 12);
    },
    renderCharts() {
      if (!this.$refs.freqChartRef || !this.$refs.turnoverChartRef) {
        return;
      }
      if (!this.freqChart) {
        this.freqChart = echarts.init(this.$refs.freqChartRef);
      }
      if (!this.turnoverChart) {
        this.turnoverChart = echarts.init(this.$refs.turnoverChartRef);
      }

      const freqRows = this.topChartRows("annualFrequency");
      const turnoverRows = this.topChartRows("annualTurnover");
      this.freqChart.setOption(
        this.chartOption("频率", "出入库频率(次/年)", "#1f7aec", freqRows, "annualFrequency"),
        true
      );
      this.turnoverChart.setOption(
        this.chartOption("周转率", "周转率(次/年)", "#13a86b", turnoverRows, "annualTurnover"),
        true
      );
      this.onResize();
    },
    chartOption(seriesName, yName, color, rows, prop) {
      const names = this.chartAxisLabels(rows);
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            const p = Array.isArray(params) ? params[0] : params;
            if (!p) {
              return "";
            }
            const row = rows[p.dataIndex] || {};
            const shed = row.shedName ? "煤棚：" + row.shedName + "<br/>" : "";
            const loc = row.locationName ? "堆位：" + row.locationName + "<br/>" : "";
            const owner = row.custName ? "客户：" + row.custName + "<br/>" : "";
            const cargo = row.cargoName ? "物资：" + row.cargoName + "<br/>" : "";
            return shed + loc + owner + cargo + p.seriesName + "：" + (p.value != null ? p.value : "");
          },
        },
        grid: { containLabel: true, left: "3%", right: "3%", top: 28, bottom: 8 },
        xAxis: {
          type: "category",
          data: names,
          axisLabel: { rotate: names.length > 8 ? 35 : 0, interval: 0, fontSize: 10 },
        },
        yAxis: { type: "value", name: yName, nameTextStyle: { fontSize: 11 } },
        series: [
          {
            name: seriesName,
            type: "bar",
            data: rows.map((row) => this.toNumber(row[prop])),
            itemStyle: { color, borderRadius: [4, 4, 0, 0] },
            label: { show: names.length <= 10, position: "top", fontSize: 10 },
          },
        ],
      };
    },
  },
  watch: {
    filteredRows() {
      this.$nextTick(() => this.renderCharts());
    },
  },
};
</script>

<style scoped lang="scss">
.warehouse-opt-page {
  padding: 16px;
  min-height: 100%;
  background: #f3f6fa;
  color: #1f2937;
}

.filter-panel,
.panel,
.metric-card,
.priority-item,
.impact-item {
  border: 1px solid #dde5ef;
  border-radius: 6px;
  background: #fff;
}

.filter-panel,
.panel {
  margin-bottom: 12px;
  padding: 14px 16px;
}

.filter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
}

.field label {
  display: block;
  margin-bottom: 6px;
  color: #475467;
  font-size: 13px;
}

.field ::v-deep .el-select,
.field ::v-deep .el-date-editor.el-input {
  width: 100%;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  white-space: nowrap;
}

.metric-grid {
  display: grid;
  grid-template-columns: 1.35fr repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.metric-card {
  min-height: 96px;
  padding: 12px;
}

.score-card {
  display: grid;
  grid-template-columns: 86px 1fr;
  gap: 12px;
  align-items: center;
}

.score-ring {
  position: relative;
  width: 86px;
  height: 86px;
  border-radius: 50%;
}

.score-ring::after {
  position: absolute;
  inset: 10px;
  border-radius: 50%;
  background: #fff;
  content: "";
}

.score-ring span {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0b8f5a;
  font-size: 23px;
  font-weight: 700;
}

.metric-name {
  color: #667085;
  font-size: 13px;
}

.metric-value {
  margin-top: 7px;
  color: #111827;
  font-size: 23px;
  font-weight: 600;
}

.metric-note {
  margin-top: 5px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.45;
}

.metric-trend {
  margin-top: 6px;
  font-size: 12px;
}

.primary {
  color: #1f7aec;
}

.green {
  color: #0b8f5a;
}

.orange,
.warning {
  color: #d46b08;
}

.danger {
  color: #c8382b;
}

.workbench-grid {
  display: grid;
  grid-template-columns: 1.55fr .9fr;
  gap: 12px;
}

.chart-grid,
.suggest-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.section-title {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.section-tip {
  margin-top: 4px;
  color: #667085;
  font-size: 12px;
}

.legend {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #667085;
  font-size: 12px;
  white-space: nowrap;
}

.legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.legend-dot.hot {
  border: 1px solid #e9b4ac;
  background: #fff2f0;
}

.legend-dot.busy {
  border: 1px solid #ffd59a;
  background: #fff8e7;
}

.legend-dot.steady {
  border: 1px solid #a7d8c0;
  background: #eefbf4;
}

.legend-dot.low {
  border: 1px solid #b9cff2;
  background: #eff6ff;
}

.heat-wrap {
  display: grid;
  gap: 8px;
}

.shed-row {
  display: grid;
  grid-template-columns: 76px 1fr;
  gap: 8px;
}

.shed-label {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 78px;
  border: 1px solid #dde5ef;
  border-radius: 4px;
  background: #f8fafc;
  color: #475467;
  font-weight: 600;
  text-align: center;
}

.heat-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(92px, 1fr));
  gap: 8px;
}

.heat-cell {
  position: relative;
  min-height: 78px;
  padding: 9px;
  border: 1px solid #d8e1ec;
  border-radius: 4px;
  background: #f8fafc;
  cursor: pointer;
}

.heat-cell.hot {
  border-color: #e9b4ac;
  background: #fff2f0;
}

.heat-cell.busy {
  border-color: #ffd59a;
  background: #fff8e7;
}

.heat-cell.steady {
  border-color: #a7d8c0;
  background: #eefbf4;
}

.heat-cell.low {
  border-color: #b9cff2;
  background: #eff6ff;
}

.heat-cell.review {
  box-shadow: inset 0 0 0 2px rgba(212, 107, 8, .28);
}

.cell-rate {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 38px;
  height: 20px;
  border-radius: 10px;
  background: rgba(255, 255, 255, .82);
  color: #475467;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.cell-code {
  padding-right: 40px;
  color: #111827;
  font-size: 15px;
  font-weight: 700;
}

.cell-meta {
  margin-top: 5px;
  color: #475467;
  font-size: 12px;
  line-height: 1.35;
}

.priority-list {
  display: grid;
  gap: 10px;
}

.priority-item {
  padding: 11px 12px;
}

.priority-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.priority-title {
  color: #111827;
  font-weight: 600;
}

.priority-desc {
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.chart-container {
  width: 100%;
  height: 250px;
}

.impact-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.impact-item {
  min-height: 82px;
  padding: 12px;
}

.impact-name {
  color: #667085;
  font-size: 13px;
}

.impact-value {
  margin-top: 6px;
  color: #111827;
  font-size: 22px;
  font-weight: 600;
}

.impact-note {
  margin-top: 5px;
  color: #98a2b3;
  font-size: 12px;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;
}

.review-card {
  min-height: 78px;
  padding: 10px 12px;
  border: 1px dashed #cfd8e3;
  border-radius: 4px;
  background: #fbfcfe;
}

.review-card strong {
  display: block;
  margin-bottom: 5px;
  color: #111827;
}

.review-card p {
  margin: 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.table-head {
  align-items: flex-end;
}

.table-tabs {
  min-width: 360px;
}

.empty-box {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cfd8e3;
  border-radius: 4px;
  color: #98a2b3;
  background: #fbfcfe;
}

@media (max-width: 1200px) {
  .filter-grid,
  .metric-grid,
  .workbench-grid,
  .chart-grid,
  .suggest-grid,
  .review-grid {
    grid-template-columns: 1fr;
  }

  .heat-grid {
    grid-template-columns: repeat(3, minmax(92px, 1fr));
  }
}
</style>
